import React, { useRef, useState } from 'react';
import { Link } from 'gatsby';
import Slider from 'react-slick';
import styled, { keyframes } from 'styled-components';
import Img from 'gatsby-image';
import Branded from '../components/Branded';

const kenBurns = keyframes`
  from { transform: scale(1.02); }
  to { transform: scale(1.08); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

const HeroWrapper = styled.section`
  position: relative;
  background: #1378e5;

  .slick-slider,
  .slick-list,
  .slick-track {
    height: 100vh;
  }

  .slick-slide > div {
    height: 100%;
  }

  .slide {
    position: relative;
    height: 100vh;
    overflow: hidden;
  }

  .slide-media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    animation: ${kenBurns} 10s ease-out forwards;
  }

  .slide-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .slide-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.1) 25%,
      rgba(0, 0, 0, 0.15) 75%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 2;
  }

  .slide-content {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 24px;
  }

  .slide-title {
    /* Brand blue is too dark against the hero imagery */
    .brand-space {
      color: #8fc4f7;
    }

    font-size: 4.5rem;
    font-weight: 300;
    color: #fff;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.1;
    margin: 0;
    opacity: 0;
    animation: ${fadeIn} 1s ease 0.2s forwards;

    @media (max-width: 1024px) {
      font-size: 3rem;
    }

    @media (max-width: 640px) {
      font-size: 2rem;
      letter-spacing: 0.04em;
    }
  }

  .slide-line {
    width: 1px;
    height: 80px;
    background: rgba(255, 255, 255, 0.25);
    margin: 32px 0;
    transform-origin: top;
    transform: scaleY(0);
    animation: ${slideDown} 0.6s ease 0.6s forwards;

    @media (max-width: 640px) {
      height: 50px;
      margin: 24px 0;
    }
  }

  .slide-cta {
    opacity: 0;
    animation: ${fadeIn} 0.8s ease 0.9s forwards;

    a {
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #fff;
      text-decoration: none;
      padding-bottom: 2px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.7);
      transition: border-color 0.3s ease, opacity 0.3s ease;

      &:hover {
        border-color: #fff;
        opacity: 0.8;
      }
    }
  }
`;

const NavDots = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    bottom: 24px;
    gap: 10px;
  }
`;

const Dot = styled.button`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  background: ${({ active }) =>
    active ? '#F08020' : 'rgba(255,255,255,0.35)'};
  cursor: pointer;
  padding: 0;
  transition: all 0.4s ease;
  transform: ${({ active }) => (active ? 'scale(1.4)' : 'scale(1)')};

  &:hover {
    background: rgba(255, 255, 255, 0.7);
  }
`;

export default function Hero({ slides }) {
  const sliderRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const items = slides || [];

  if (!items.length) return null;

  const settings = {
    arrows: false,
    dots: false,
    fade: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: false,
    cssEase: 'ease-in-out',
    beforeChange: (_, next) => setCurrent(next),
  };

  return (
    <HeroWrapper>
      <Slider ref={sliderRef} {...settings}>
        {items.map(({ _key, title, image, video, mediaType, project }) => {
          const projectSlug = project?.slug?.current;
          const slideTitle = title || project?.title || '';
          const linkTo = projectSlug
            ? `/portfolio/${projectSlug}`
            : '/portfolio';

          return (
            <div key={_key}>
              <div className="slide">
                <div className="slide-overlay" />
                {mediaType === 'video' && video?.asset?.url ? (
                  <video
                    className="slide-video"
                    src={video.asset.url}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : image?.asset?.fluid ? (
                  <div className="slide-media">
                    <Img
                      style={{ height: '100%', width: '100%' }}
                      imgStyle={{ objectFit: 'cover' }}
                      fluid={image.asset.fluid}
                    />
                  </div>
                ) : null}
                <div className="slide-content">
                  <Branded as="h1" className="slide-title" text={slideTitle} />
                  <div className="slide-line" />
                  <div className="slide-cta">
                    <Link to={linkTo}>View Project</Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>

      <NavDots>
        {items.map((_, idx) => (
          <Dot
            key={idx}
            active={idx === current}
            onClick={() => sliderRef.current?.slickGoTo(idx)}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </NavDots>
    </HeroWrapper>
  );
}
